import { onBeforeUnmount, onMounted, type Ref } from 'vue'

/**
 * Gives a screen reader the same empty-state label sighted users see.
 *
 * @ui defect: `Input` (contentType="tags") ignores the `placeholder` prop and
 * hard-codes a literal "No options" `<span class="input__placeholder">` in the
 * DOM whenever nothing is selected. theme.css hides that text visually and
 * paints the real label via `--jf-empty-label`, but the hidden text is still in
 * the accessibility tree — a screen reader reads "no options" for a field that
 * in fact has plenty.
 *
 * This hides that span from the accessibility tree and mirrors the same label
 * onto the focusable `.input__tags` control's `aria-label`, so its accessible
 * name matches what is painted on screen. Once a value is picked, the
 * placeholder span is torn down by the library and the label is cleared here
 * too, so the control's name reverts to its selected chips rather than staying
 * stuck on the empty-state text.
 *
 * Delete this the day @ui honours `placeholder` in tags mode.
 */
export const useAccessibleTagsEmptyLabel = (
  root: Ref<HTMLElement | null>,
  emptyLabel: () => string,
): void => {
  const sync = (): void => {
    const el = root.value
    if (!el) return
    const placeholder = el.querySelector('.input__placeholder')
    const tags = el.querySelector('.input__tags')
    placeholder?.setAttribute('aria-hidden', 'true')
    if (!tags) return
    if (placeholder) tags.setAttribute('aria-label', emptyLabel())
    else tags.removeAttribute('aria-label')
  }

  let observer: MutationObserver | null = null

  onMounted(() => {
    sync()
    if (!root.value) return
    observer = new MutationObserver(sync)
    observer.observe(root.value, { childList: true, subtree: true })
  })

  onBeforeUnmount(() => observer?.disconnect())
}
